import React, { useRef, useState } from 'react';
import pluralize from 'pluralize';
import { FieldArray, Formik } from 'formik';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import { isNil } from 'lodash';

import BaseTableHeader from 'components/wrappers/BaseTableHeader';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import SubmitButton from 'components/formik/SubmitButton';
import {
  getListOfCustomersWithoutAgreement,
  mapClientBulkImportToRequest,
} from 'utils/clientUtils';
import {
  AdditionalInfoModel,
  BillingInformationModel,
  CreateCustomerGeneralInfo,
  CreateCustomerRequest,
  ServiceAgreementTypeEnum,
} from 'api/backofficeApiClient';
import { BaseUploadFile } from 'components/wrappers/BaseFileUpload';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import ClientModal, {
  ClientInitialValues,
} from 'components/modals/ClientModal';
import { ClientCreationEnum } from 'components/modals/ClientModal/enum';
import ClientBulkImportTableForm from './ClientBulkImportTableForm';
import { mapCustomerDetailsToInitialValues } from '../../ClientDetailsPage/EditClient/utils';
import { ServiceAgreementFileInfoType } from 'components/modals/ClientModal/types';

type Props = {
  onSubmit: (data: CreateCustomerRequest[], memberId?: string) => void;
  allCustomers: CreateCustomerDetailsModel[];
};

const agreementValidationSchema = Yup.object({
  agreementDetailsInfo: Yup.object({
    agreementDetailsFileKey: Yup.array().when('agreementDetailsType', {
      is: (agreementDetailsType: ServiceAgreementTypeEnum) =>
        agreementDetailsType === ServiceAgreementTypeEnum.ClientAgreement ||
        agreementDetailsType === ServiceAgreementTypeEnum.MemberAgreement,
      then: Yup.array().of(Yup.string().defined()).min(1, 'Field is required'),
    }),
  }),
});

const validationSchema = Yup.object({
  customers: Yup.array().of(agreementValidationSchema),
});

export interface EditCustomerDetailsModel {
  id: string;
  generalInfo: CreateCustomerGeneralInfo;
  additionalInfo: AdditionalInfoModel;
  agreementDetailsInfo: {
    agreementDetailsType?: ServiceAgreementTypeEnum;
    agreementDetailsFileKey?: string;
    agreementDetailsFileName?: string;
  };
  billingInfo: BillingInformationModel;
}

export interface CreateCustomerDetailsModel {
  id: string;
  generalInfo: CreateCustomerGeneralInfo;
  additionalInfo: AdditionalInfoModel;
  agreementDetailsInfo: {
    agreementDetailsType?: ServiceAgreementTypeEnum;
    agreementDetailsFileKey?: string[];
    agreementDetailsFileName?: string;
  };
  billingInfo: BillingInformationModel;
}

export interface ClientBulkImportForm {
  customers: CreateCustomerDetailsModel[];
}

export interface UploadedFileModel {
  file: BaseUploadFile[];
  clientId: string;
}

interface ClientModel {
  data: ClientInitialValues;
  clientId: string;
}

export default function ClientBulkImportTableSection(props: Props) {
  const { allCustomers: preAllCustomers, onSubmit } = props;
  const isAdmin = useAppSelector(isAdminSelector);
  const [clientDetails, setClientDetails] = useState<ClientModel>();
  const [listOfUploadedFiles, setListOfUploadedFiles] = useState<
    UploadedFileModel[]
  >([]);
  const [allCustomers, setAllCustomers] =
    useState<CreateCustomerDetailsModel[]>(preAllCustomers);
  const initialValues: ClientBulkImportForm = {
    customers: allCustomers,
  };
  const formRef: any = useRef();

  const handleFileChange = (
    files: BaseUploadFile[],
    clientId: string,
    values?: CreateCustomerRequest,
  ) => {
    const hasFileInTheList = listOfUploadedFiles.find(
      (file) => file.clientId === clientId,
    );

    if (
      hasFileInTheList &&
      (values?.agreementDetailsInfo.agreementDetailsType ===
        ServiceAgreementTypeEnum.NotAvailable ||
        values?.agreementDetailsInfo.agreementDetailsType ===
          ServiceAgreementTypeEnum.MemberAgreement)
    ) {
      const newList = listOfUploadedFiles
        .filter((file) => file.clientId !== clientId)
        .map((file) => file);

      return setListOfUploadedFiles(newList);
    }

    if (hasFileInTheList) {
      const newList = listOfUploadedFiles.map((file) =>
        file.clientId === clientId ? { clientId: clientId, file: files } : file,
      );
      return setListOfUploadedFiles(newList);
    }

    const newFile: UploadedFileModel = { clientId, file: files };
    const listOfUploadedFilesDuplicate = [...listOfUploadedFiles];
    listOfUploadedFilesDuplicate.push(newFile);

    setListOfUploadedFiles(listOfUploadedFilesDuplicate);
  };

  const handleEditClientModalOpen = (
    clientDetails: CreateCustomerDetailsModel,
  ) => {
    const { id: clientId } = clientDetails;

    const updatedDetails: EditCustomerDetailsModel = {
      ...clientDetails,
      agreementDetailsInfo: {
        ...clientDetails.agreementDetailsInfo,
        agreementDetailsFileKey: clientDetails.agreementDetailsInfo
          .agreementDetailsFileKey?.length
          ? clientDetails.agreementDetailsInfo.agreementDetailsFileKey[0]
          : undefined,
      },
    };

    setClientDetails({
      data: mapCustomerDetailsToInitialValues(
        updatedDetails,
        ClientCreationEnum.EditImport,
      ),
      clientId,
    });
  };

  const handleEditClientModalClose = () => {
    setClientDetails(undefined);
  };

  const customersMessage = pluralize(
    'Customer',
    preAllCustomers?.length || 0,
    false,
  );

  const getAllCustomersCountMessage = () => {
    const allCustomerLength = preAllCustomers?.length || 0;

    const msg = pluralize('Record', allCustomerLength, true);
    return msg + ' to be added';
  };

  const getCustomersCountMessage = () => {
    const customersLength =
      getListOfCustomersWithoutAgreement(initialValues.customers).length || 0;
    const msg = pluralize('Agreements', customersLength, true);

    return msg + ' to be uploaded';
  };

  const renderSubTitle = () => (
    <>
      <div>{getAllCustomersCountMessage()}</div>
      <div>{getCustomersCountMessage()}</div>
    </>
  );

  const handleSubmit = async (data: ClientBulkImportForm) => {
    const values = mapClientBulkImportToRequest(preAllCustomers, data);
    await onSubmit(values);
  };

  const onSetClientValues = (
    values: CreateCustomerRequest,
    customerId: string,
    file: BaseUploadFile[],
  ) => {
    handleFileChange(file, customerId, values);

    const formValues: CreateCustomerDetailsModel[] =
      formRef.current.values.customers || [];

    const newAllCustomers = formValues.map((customer) =>
      customer.id === customerId
        ? {
            ...values,
            id: customerId,
            agreementDetailsInfo: {
              ...values.agreementDetailsInfo,
              agreementDetailsFileKey: !isNil(
                values.agreementDetailsInfo.agreementDetailsFileKey,
              )
                ? [values.agreementDetailsInfo.agreementDetailsFileKey]
                : undefined,
            },
          }
        : customer,
    );
    setAllCustomers(newAllCustomers);
  };

  const getServiceAgreementFileInfo = (
    clientDetails: ClientModel,
  ): ServiceAgreementFileInfoType => {
    const uploadedFile = listOfUploadedFiles.find(
      (file) => file.clientId === clientDetails?.clientId,
    );

    return {
      type: clientDetails.data.serviceAgreement.serviceAgreement,
      files: uploadedFile?.file || [],
    };
  };

  return (
    <>
      {!isNil(clientDetails) && (
        <ClientModal
          onSetClientValues={onSetClientValues}
          onClose={handleEditClientModalClose}
          creationType={ClientCreationEnum.EditImport}
          initialValues={clientDetails.data}
          customerId={clientDetails.clientId}
          isDraftAvailable={false}
          serviceAgreementFileInfo={getServiceAgreementFileInfo(clientDetails)}
        />
      )}
      <BasePageLabel label="Upload agreements" />
      <div className="page-box page-inner">
        <div className="page-inner-container">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            innerRef={formRef}
          >
            <Form>
              <BaseTableHeader
                title={customersMessage}
                enableSubTitle={true}
                subTitle={renderSubTitle()}
              >
                <div>
                  <SubmitButton>Import</SubmitButton>
                </div>
              </BaseTableHeader>
              <FieldArray
                name="customers"
                render={(props) => (
                  <ClientBulkImportTableForm
                    {...props}
                    isAdmin={isAdmin as boolean}
                    handleEditClientModalOpen={handleEditClientModalOpen}
                    handleFileChange={handleFileChange}
                    listOfUploadedFiles={listOfUploadedFiles}
                  />
                )}
              />
            </Form>
          </Formik>
        </div>
      </div>
    </>
  );
}
